import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import GoalProgressCard from '@/components/GoalProgressCard';
import { 
  Scissors, LogOut, Clock, Calendar, User, 
  CheckCircle, DollarSign, Play, Square, AlertCircle, KeyRound, X, Heart, Package, Eye, EyeOff
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-500',
  confirmed: 'bg-green-500/20 border-green-500/50 text-green-500',
  completed: 'bg-zinc-500/20 border-zinc-500/50 text-zinc-400',
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  confirmed: 'Confirmada',
  completed: 'Completada',
};

export default function BarberDashboard() {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [tipsData, setTipsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinAction, setPinAction] = useState('check-in');
  const [pinValue, setPinValue] = useState('');
  const [showPin, setShowPin] = useState(false);
  // New: panel state machine (SIN_CAJA / BLOQUEADO_ENTRADA / ACTIVO / BLOQUEADO_SALIDA / DESCANSO)
  const [panelState, setPanelState] = useState(null);

  const fetchPanelState = async () => {
    try {
      const res = await axios.get(`${API}/attendance/barber-state`, getAuthHeaders());
      setPanelState(res.data);
    } catch (_e) { /* noop */ }
  };

  useEffect(() => {
    if (user?.role !== 'barber') {
      navigate('/login');
      return;
    }
    fetchDashboard();
    fetchTips();
    fetchPanelState();
    const iv = setInterval(fetchPanelState, 30000); // poll every 30s
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handlePinSubmit = async () => {
    if (pinValue.length !== 4) {
      toast.error('Ingresa el código de 4 dígitos');
      return;
    }
    setCheckingIn(true);
    try {
      const response = await axios.post(
        `${API}/attendance/code-check`,
        { code: pinValue },
        getAuthHeaders()
      );
      toast.success(response.data.message);
      setShowPinDialog(false);
      setPinValue('');
      fetchDashboard();
      fetchPanelState();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar código');
    } finally {
      setCheckingIn(false);
    }
  };

  const openPinDialog = (action) => {
    setPinAction(action);
    setPinValue('');
    setShowPinDialog(true);
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/barber/dashboard`, getAuthHeaders());
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchTips = async () => {
    try {
      const response = await axios.get(`${API}/barber/tips`, getAuthHeaders());
      setTipsData(response.data);
    } catch (error) {
      // error handled
    }
  };

  const markAsCompleted = async (appointmentId) => {
    try {
      await axios.put(
        `${API}/appointments/${appointmentId}/status?status=completed`,
        {},
        getAuthHeaders()
      );
      toast.success('Cita completada');
      fetchDashboard();
    } catch (error) {
      toast.error('Error al actualizar cita');
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-zinc-500">Cargando...</p>
      </div>
    );
  }

  const { barber, today_date, appointments, attendance, monthly_commissions } = dashboardData || {};
  const hasCheckedIn = !!attendance;
  const hasCheckedOut = attendance?.check_out;

  // State-based lock overlay (except ACTIVO which shows full panel)
  const state = panelState?.state;
  const showLockOverlay = state && state !== 'ACTIVO';
  const overlayMap = {
    SIN_CAJA: {
      title: 'Espera la apertura de caja',
      msg: 'Tu sucursal aún no ha abierto la caja del día. En cuanto el encargado la abra, podrás registrar tu entrada.',
      showButton: false,
      color: 'text-yellow-400',
      Icon: Clock,
    },
    BLOQUEADO_ENTRADA: {
      title: 'Registra tu entrada',
      msg: 'Pídele al cajero/encargado el código de asistencia y tecléalo para desbloquear tu panel.',
      showButton: true,
      buttonLabel: 'Registrar entrada',
      color: 'text-gold',
      Icon: KeyRound,
    },
    BLOQUEADO_SALIDA: {
      title: 'Turno cerrado',
      msg: 'Ya registraste tu salida hoy. Tu panel se reactivará en la próxima apertura de caja.',
      showButton: false,
      color: 'text-zinc-400',
      Icon: CheckCircle,
    },
    DESCANSO: {
      title: 'Día de descanso',
      msg: 'Hoy es tu día de descanso. Disfrútalo. 🎉',
      showButton: false,
      color: 'text-emerald-400',
      Icon: Heart,
    },
  };
  const overlay = showLockOverlay ? overlayMap[state] : null;
  const OverlayIcon = overlay?.Icon;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="barber-dashboard">
      {/* Header */}
      <header className="h-16 border-b border-zinc-800 flex items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_8f620d75-9fbb-40ab-bbdd-47d8eeebdc0d/artifacts/z1jrqp78_IMG_0475.jpeg" 
            alt="The Holland"
            className="w-10 h-10 object-contain"
          />
          <div>
            <h1 className="font-heading text-lg text-white">Mi Panel</h1>
            <p className="text-xs text-zinc-500">{barber?.branch_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-white">{barber?.name}</p>
            <p className="text-xs text-gold">{barber?.commission_percentage}% comisión</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="text-zinc-400 hover:text-white"
            data-testid="barber-logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className={`max-w-5xl mx-auto p-6 space-y-6 ${showLockOverlay ? 'blur-sm pointer-events-none select-none' : ''}`} data-testid="barber-main">
        {/* Date & Check-in */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Card className="flex-1 bg-zinc-900/50 border-zinc-800 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-widest">Hoy</p>
                <p className="font-heading text-2xl text-white mt-1">
                  {(() => {
                    // Parse date parts to avoid timezone issues
                    const [year, month, day] = today_date.split('-').map(Number);
                    const date = new Date(year, month - 1, day);
                    return date.toLocaleDateString('es-MX', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long' 
                    });
                  })()}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-gold" />
            </div>
          </Card>

          <Card className="flex-1 bg-zinc-900/50 border-zinc-800 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-widest">Asistencia</p>
                {hasCheckedIn ? (
                  <div className="mt-1">
                    <p className="text-green-500 text-sm flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Check-in: {formatTime(attendance.check_in)}
                    </p>
                    {hasCheckedOut && (
                      <p className="text-red-500 text-sm">
                        Check-out: {formatTime(attendance.check_out)}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-yellow-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Sin check-in
                  </p>
                )}
              </div>
              {!hasCheckedIn ? (
                <Button
                  onClick={() => openPinDialog('check-in')}
                  disabled={checkingIn}
                  className="bg-green-500 hover:bg-green-600 text-white"
                  data-testid="check-in-btn"
                >
                  <KeyRound className="w-4 h-4 mr-2" />
                  Check In
                </Button>
              ) : !hasCheckedOut ? (
                <Button
                  onClick={() => openPinDialog('check-out')}
                  disabled={checkingIn}
                  variant="outline"
                  className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                  data-testid="check-out-btn"
                >
                  <KeyRound className="w-4 h-4 mr-2" />
                  Check Out
                </Button>
              ) : (
                <CheckCircle className="w-8 h-8 text-green-500" />
              )}
            </div>
          </Card>
        </div>

        {/* Today's Appointments */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
            <h2 className="font-heading text-lg text-white flex items-center gap-2">
              <Scissors className="w-5 h-5 text-gold" />
              Agenda del Día
            </h2>
            <span className="text-sm text-zinc-500">{appointments?.length || 0} citas</span>
          </div>
          
          {appointments?.length === 0 ? (
            <div className="p-12 text-center">
              <Scissors className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500">No tienes citas agendadas para hoy</p>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="divide-y divide-zinc-800">
                {appointments?.map((apt) => (
                  <div key={apt.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-16 text-center">
                          <p className="text-2xl font-heading text-gold">{apt.time}</p>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <User className="w-4 h-4 text-zinc-500" />
                            <span className="text-white font-medium">{apt.client_name}</span>
                          </div>
                          {apt.service_name && (
                            <div className="flex items-center gap-2">
                              <Scissors className="w-4 h-4 text-zinc-500" />
                              <span className="text-zinc-400 text-sm">{apt.service_name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`px-2 py-1 rounded-sm text-xs border ${STATUS_COLORS[apt.status]}`}>
                          {STATUS_LABELS[apt.status]}
                        </span>
                        {apt.status !== 'completed' && (
                          <Button
                            size="sm"
                            onClick={() => markAsCompleted(apt.id)}
                            className="bg-green-500/20 text-green-500 hover:bg-green-500/30"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Completar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </Card>

        {/* Monthly Commissions - Services */}
        <Card className="bg-zinc-900/50 border-zinc-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg text-white flex items-center gap-2">
              <Scissors className="w-5 h-5 text-gold" />
              Comisiones por Servicios
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-zinc-800/50 rounded-sm">
              <p className="text-xs text-zinc-500 uppercase">Servicios Realizados</p>
              <p className="text-2xl text-white font-semibold mt-1">
                {monthly_commissions?.total_services || 0}
              </p>
            </div>
            <div className="p-4 bg-zinc-800/50 rounded-sm">
              <p className="text-xs text-zinc-500 uppercase">Total Facturado</p>
              <p className="text-2xl text-white font-semibold mt-1">
                ${(monthly_commissions?.total_services_revenue || 0).toFixed(2)}
              </p>
            </div>
            <div className="p-4 bg-gold/10 border border-gold/30 rounded-sm">
              <p className="text-xs text-gold uppercase">Mi Comisión ({barber?.commission_percentage}%)</p>
              <p className="text-2xl text-gold font-heading mt-1">
                ${(monthly_commissions?.total_services_commission || 0).toFixed(2)}
              </p>
            </div>
          </div>

          {monthly_commissions?.services_breakdown?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-zinc-800">
              <p className="text-xs text-zinc-500 uppercase tracking-widest mb-3">Últimos servicios</p>
              <div className="space-y-2">
                {monthly_commissions.services_breakdown.slice(0, 5).map((item, i) => (
                  <div key={item.sale_id || `svc-${item.date}-${i}`} className="flex items-center justify-between p-2 bg-zinc-800/30 rounded-sm">
                    <div>
                      <p className="text-sm text-white">{item.item_name}</p>
                      <p className="text-xs text-zinc-500">{item.date}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-gold font-semibold">+${item.commission.toFixed(2)}</span>
                      <p className="text-xs text-zinc-500">${item.price.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Monthly Commissions - Products */}
        <Card className="bg-zinc-900/50 border-zinc-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg text-white flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-500" />
              Comisiones por Productos
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-zinc-800/50 rounded-sm">
              <p className="text-xs text-zinc-500 uppercase">Productos Vendidos</p>
              <p className="text-2xl text-white font-semibold mt-1">
                {monthly_commissions?.total_products || 0}
              </p>
            </div>
            <div className="p-4 bg-zinc-800/50 rounded-sm">
              <p className="text-xs text-zinc-500 uppercase">Total Facturado</p>
              <p className="text-2xl text-white font-semibold mt-1">
                ${(monthly_commissions?.total_products_revenue || 0).toFixed(2)}
              </p>
            </div>
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-sm">
              <p className="text-xs text-blue-400 uppercase">Mi Comisión (10%)</p>
              <p className="text-2xl text-blue-400 font-heading mt-1">
                ${(monthly_commissions?.total_products_commission || 0).toFixed(2)}
              </p>
            </div>
          </div>

          {monthly_commissions?.products_breakdown?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-zinc-800">
              <p className="text-xs text-zinc-500 uppercase tracking-widest mb-3">Últimos productos</p>
              <div className="space-y-2">
                {monthly_commissions.products_breakdown.slice(0, 5).map((item, i) => (
                  <div key={item.sale_id || `prod-${item.date}-${i}`} className="flex items-center justify-between p-2 bg-blue-500/10 rounded-sm border border-blue-500/20">
                    <div>
                      <p className="text-sm text-white">{item.item_name}</p>
                      <p className="text-xs text-zinc-500">{item.date}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-blue-400 font-semibold">+${item.commission.toFixed(2)}</span>
                      <p className="text-xs text-zinc-500">${item.price.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(!monthly_commissions?.products_breakdown || monthly_commissions.products_breakdown.length === 0) && (
            <div className="mt-4 pt-4 border-t border-zinc-800 text-center py-4">
              <p className="text-zinc-500 text-sm">No has vendido productos este mes</p>
            </div>
          )}
        </Card>

        {/* Personal Goal Progress */}
        <GoalProgressCard barberId={user?.id} getAuthHeaders={getAuthHeaders} />

        {/* Total Commission Summary */}
        <Card className="bg-gradient-to-r from-gold/10 to-blue-500/10 border-gold/30 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-zinc-400 uppercase tracking-widest">Total Comisiones del Mes</p>
              <p className="text-3xl font-heading text-white mt-1">
                ${(monthly_commissions?.total_commission || 0).toFixed(2)}
              </p>
            </div>
            <div className="text-right space-y-1">
              <p className="text-sm">
                <span className="text-gold">Servicios: ${(monthly_commissions?.total_services_commission || 0).toFixed(2)}</span>
              </p>
              <p className="text-sm">
                <span className="text-blue-400">Productos: ${(monthly_commissions?.total_products_commission || 0).toFixed(2)}</span>
              </p>
            </div>
          </div>
        </Card>

        {/* Tips Section */}
        {tipsData && (tipsData.total_pending > 0 || tipsData.paid_tips?.length > 0) && (
          <Card className="bg-zinc-900/50 border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg text-white flex items-center gap-2">
                <Heart className="w-5 h-5 text-pink-500" />
                Propinas
              </h2>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="p-4 bg-pink-500/10 border border-pink-500/30 rounded-sm">
                <p className="text-xs text-pink-500 uppercase">Pendientes por Cobrar</p>
                <p className="text-2xl text-pink-500 font-heading mt-1">
                  ${(tipsData?.total_pending || 0).toFixed(2)}
                </p>
                {tipsData?.total_pending_fee > 0 && (
                  <div className="mt-2 pt-2 border-t border-pink-500/20 text-xs">
                    <div className="flex justify-between text-zinc-400">
                      <span>Propina bruta:</span>
                      <span>${(tipsData?.total_pending_gross || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-red-400">
                      <span>Comisión tarjeta:</span>
                      <span>-${(tipsData?.total_pending_fee || 0).toFixed(2)}</span>
                    </div>
                  </div>
                )}
                <p className="text-xs text-zinc-500 mt-2">
                  {tipsData?.pending_tips?.length || 0} propina(s)
                </p>
              </div>
              <div className="p-4 bg-zinc-800/50 rounded-sm">
                <p className="text-xs text-zinc-500 uppercase">Total Cobrado</p>
                <p className="text-2xl text-white font-semibold mt-1">
                  ${(tipsData?.total_paid || 0).toFixed(2)}
                </p>
              </div>
            </div>

            {tipsData?.pending_tips?.length > 0 && (
              <div className="pt-4 border-t border-zinc-800">
                <p className="text-xs text-zinc-500 uppercase tracking-widest mb-3">Propinas pendientes</p>
                <div className="space-y-2">
                  {tipsData.pending_tips.slice(0, 5).map((tip, i) => (
                    <div key={tip.sale_id || `tip-${i}`} className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-sm">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-white">{tip.branch_name || 'Sucursal'}</p>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            tip.card_type === 'TDC' 
                              ? 'bg-purple-500/20 text-purple-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {tip.card_type === 'TDC' ? 'Crédito' : 'Débito'}
                          </span>
                        </div>
                        <p className="text-xs text-zinc-500">{tip.date}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-pink-500 font-semibold">${tip.tip_net?.toFixed(2) || tip.amount?.toFixed(2)}</span>
                        {tip.tip_fee > 0 && (
                          <p className="text-[10px] text-zinc-500">
                            (${tip.amount?.toFixed(2)} - ${tip.tip_fee?.toFixed(2)})
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-3 text-center">
                  Solicita el pago de tus propinas al administrador
                </p>
              </div>
            )}
          </Card>
        )}
      </main>

      {/* Locked panel overlay (state-based) */}
      {overlay && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-md p-6"
          data-testid={`barber-lock-overlay-${state}`}
        >
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg max-w-md w-full p-8 text-center space-y-5">
            {OverlayIcon && <OverlayIcon className={`w-14 h-14 mx-auto ${overlay.color}`} />}
            <div>
              <h2 className="font-heading text-2xl text-white mb-2">{overlay.title}</h2>
              <p className="text-sm text-zinc-400 leading-relaxed">{overlay.msg}</p>
              {panelState?.locked_out_seconds ? (
                <p className="text-xs text-red-400 mt-3">
                  Bloqueado por intentos fallidos: {Math.ceil(panelState.locked_out_seconds / 60)} min restantes
                </p>
              ) : null}
            </div>
            {overlay.showButton && (
              <Button
                onClick={() => { setPinAction('check-in'); setPinValue(''); setShowPinDialog(true); }}
                className="w-full h-14 text-lg bg-gold text-black hover:bg-gold/90"
                data-testid="barber-open-code-dialog-btn"
              >
                <KeyRound className="w-5 h-5 mr-2" />
                {overlay.buttonLabel}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Attendance Code Dialog (was PIN dialog) */}
      <Dialog open={showPinDialog} onOpenChange={setShowPinDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white text-center flex items-center justify-center gap-2">
              <KeyRound className="w-5 h-5 text-gold" />
              Código de Asistencia
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-center text-zinc-400 text-sm">
              Teclea el código de 4 dígitos que aparece en el POS de tu sucursal.
            </p>
            <div className="relative">
              <Input
                type={showPin ? 'text' : 'password'}
                inputMode="numeric"
                maxLength={4}
                value={pinValue}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setPinValue(val);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && pinValue.length === 4) handlePinSubmit();
                }}
                placeholder={showPin ? '0000' : '••••'}
                className="bg-zinc-800 border-zinc-700 text-white text-center text-2xl tracking-[0.5em] h-14 pr-10"
                data-testid="pin-input"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPin((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white transition-colors"
                aria-label={showPin ? 'Ocultar código' : 'Mostrar código'}
                data-testid="pin-toggle"
              >
                {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setShowPinDialog(false); setPinValue(''); }}
                className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Cancelar
              </Button>
              <Button
                onClick={handlePinSubmit}
                disabled={pinValue.length !== 4 || checkingIn}
                className="flex-1 bg-gold text-black hover:bg-gold/90"
                data-testid="pin-submit-btn"
              >
                {checkingIn ? 'Procesando...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
